import { useMemo, useState } from "react";
import type { Account, AccountType, TaxCode } from "@core/index";
import { accountTypeLabel, buildLedger, zero } from "@core/index";
import { useAppStore, selectCurrentRole } from "@state/store";
import { can } from "@data/index";
import { useLedger } from "@ui/useLedger";
import { MoneyText, LoadingSkeleton, ErrorState } from "@ui/components";

const TYPES: AccountType[] = ["asset", "liability", "equity", "income", "expense"];
const TAX_CODES: (TaxCode | "")[] = ["", "SR", "ZR", "EX", "OS"];

export function Accounts() {
  const { status, error, accounts, entries, base, entityId, scopeName } = useLedger();
  const saveAccount = useAppStore((s) => s.saveAccount);
  const load = useAppStore((s) => s.load);
  const canAddAccounts = can(useAppStore(selectCurrentRole), "account.add");

  const [adding, setAdding] = useState(false);
  // Code of the account being edited (its code is the immutable key), or null.
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ code: string; name: string; type: AccountType; taxCode: TaxCode | "" }>({
    code: "",
    name: "",
    type: "asset",
    taxCode: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const formOpen = adding || editingCode !== null;

  const ledger = useMemo(
    () => buildLedger(entries, accounts, base, entityId),
    [entries, accounts, base, entityId],
  );

  const sorted = useMemo(
    () => [...accounts].sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  function closeForm() {
    setDraft({ code: "", name: "", type: "asset", taxCode: "" });
    setFormError(null);
    setAdding(false);
    setEditingCode(null);
  }

  function startEdit(a: Account) {
    setEditingCode(a.code);
    setAdding(false);
    setFormError(null);
    setDraft({ code: a.code, name: a.name, type: a.type, taxCode: a.taxCode ?? "" });
  }

  async function submit() {
    const name = draft.name.trim();
    // When editing, the code is fixed (it keys every posted line); only add
    // validates a fresh, unique 3–5 digit code.
    const code = editingCode ?? draft.code.trim();
    if (!editingCode) {
      if (!/^\d{3,5}$/.test(code)) {
        setFormError("Code must be 3–5 digits.");
        return;
      }
      if (accounts.some((a) => a.code === code)) {
        setFormError(`Account ${code} already exists.`);
        return;
      }
    }
    if (name === "") {
      setFormError("Enter an account name.");
      return;
    }
    // Rebuild explicitly: keep the intercompany flag (not editable here) and
    // clear the tax default when blank by omitting it entirely.
    const existing = accounts.find((a) => a.code === code);
    const account: Account = {
      code,
      name,
      type: draft.type,
      ...(existing?.intercompany ? { intercompany: true as const } : {}),
      ...(draft.taxCode ? { taxCode: draft.taxCode } : {}),
    };
    await saveAccount(account);
    closeForm();
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Chart of Accounts</h1>
          <div className="sub">
            UAE-appropriate default chart · balances shown for {scopeName}. VAT control &amp; FX
            gain/loss accounts pre-wired.
          </div>
        </div>
        <div className="actions">
          {!canAddAccounts ? (
            <span className="role-note" role="note">
              Read-only — adding accounts needs an Admin or Owner role.
            </span>
          ) : (
            <button
              type="button"
              className="btn primary"
              onClick={() => (formOpen ? closeForm() : setAdding(true))}
            >
              {formOpen ? "Close" : "＋ Add account"}
            </button>
          )}
        </div>
      </div>

      {formOpen ? (
        <div className="card" style={{ marginBottom: "var(--s4)" }}>
          <div className="card-body">
            <div className="row" style={{ gap: "var(--s4)", alignItems: "flex-end" }}>
              <div className="field" style={{ width: 110 }}>
                <label htmlFor="acCode">Code</label>
                <input
                  id="acCode"
                  className="input"
                  inputMode="numeric"
                  value={draft.code}
                  disabled={editingCode !== null}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label htmlFor="acName">Account name</label>
                <input
                  id="acName"
                  className="input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
              <div className="field" style={{ width: 150 }}>
                <label htmlFor="acType">Type</label>
                <select
                  id="acType"
                  className="input"
                  value={draft.type}
                  onChange={(e) => setDraft({ ...draft, type: e.target.value as AccountType })}
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {accountTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ width: 120 }}>
                <label htmlFor="acTax">Tax default</label>
                <select
                  id="acTax"
                  className="input"
                  value={draft.taxCode}
                  onChange={(e) => setDraft({ ...draft, taxCode: e.target.value as TaxCode | "" })}
                >
                  {TAX_CODES.map((t) => (
                    <option key={t || "none"} value={t}>
                      {t || "—"}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="btn primary" onClick={submit}>
                {editingCode ? "Save changes" : "Save account"}
              </button>
            </div>
            {formError ? <div className="error-banner mt3">{formError}</div> : null}
          </div>
        </div>
      ) : null}

      {status === "loading" ? (
        <LoadingSkeleton rows={6} />
      ) : status === "error" ? (
        <ErrorState
          title="Couldn’t load the chart"
          message={error ?? "The account store didn’t respond."}
          onRetry={load}
        />
      ) : (
        <div className="card">
          <div className="card-body tight">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th>Type</th>
                    <th>Tax default</th>
                    <th className="num">Balance ({base})</th>
                    {canAddAccounts ? (
                      <th>
                        <span className="visually-hidden">Edit</span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((a) => (
                    <tr key={a.code}>
                      <td className="acctcode">{a.code}</td>
                      <td>{a.name}</td>
                      <td>
                        <span className="badge neutral">{accountTypeLabel(a.type)}</span>
                      </td>
                      <td>{a.taxCode ?? "—"}</td>
                      <td className="num">
                        <MoneyText value={ledger.get(a.code)?.balance ?? zero(base)} colorSign />
                      </td>
                      {canAddAccounts ? (
                        <td>
                          <button
                            type="button"
                            className="btn secondary sm"
                            aria-label={`Edit ${a.name}`}
                            onClick={() => startEdit(a)}
                          >
                            Edit
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
